import BannersLink from '@components/BannersLink';
import type {
  GetIndexCoreBannersQuery,
  GetRubricBannersQuery
} from '@schema/cart';
import { preparePriceFloatObject } from '@utils/floatNumbers';
import { memo } from 'react';
import { LazyLoadImage } from '@components/reactLazyLoadImageComponent';
import type { FC } from 'react';
import { useHistory } from '@compat/router';

interface RubricBannerProps {
  index?: number;
  banner:
    | NonNullable<GetRubricBannersQuery['getRubricBanners'][0]>
    | NonNullable<GetIndexCoreBannersQuery['getIndexCore']['banners']>[0];
  visibleByDefault: boolean;
  className?: string;
  onClick?: () => void;
}

const RubricBanner: FC<RubricBannerProps> = ({
  banner,
  visibleByDefault,
  className,
  onClick
}) => {
  const history = useHistory();
  if (!banner) {
    return null;
  }
  let oldPrice: { wholePart: string; floatPart: string } | undefined,
    newPrice: { wholePart: string; floatPart: string } | undefined,
    percentDiscount;
  if (banner.catalogItems) {
    oldPrice = preparePriceFloatObject(
      banner.catalogItems[0]?.priceBlockWeightPrice
        ? banner.catalogItems[0]?.oldPriceField
        : banner.catalogItems[0]?.oldPriceField2 ?? null
    );
    newPrice = preparePriceFloatObject(
      banner.catalogItems[0]?.priceBlockWeightPrice
        ? banner.catalogItems[0]?.currentPriceField
        : banner.catalogItems[0]?.currentPriceField2
    );
    percentDiscount =
      typeof banner?.catalogItems?.[0]?.oldPrice === 'number'
        ? Math.round(
            (((banner?.catalogItems?.[0]?.oldPrice || 0) -
              (banner?.catalogItems?.[0]?.price || 0)) /
              (banner?.catalogItems?.[0]?.oldPrice || 0)) *
              100
          )
        : null;
  }
  const link =
    banner.link === location.pathname
      ? typeof banner?.catalogItems?.[0]?.id === 'string'
        ? `/product/${banner.catalogItems[0].id}`
        : ''
      : banner.link || '';

  return (
    <BannersLink
      link={link}
      onClick={() => {
        history.push(link);
        onClick?.();
      }}
      className={`relative block w-full h-full overflow-hidden text-white rounded-xl ${
        className || ''
      }`}
    >
      {banner.catalogItems ? (
        <>
          <div className='absolute top-0 h-full left-0 w-1/2 rounded-xl rounded-br-none bg-[#B32442]/80 px-4 pt-4 '>
            {banner.title && (
              <div className='mb-3 text-sm font-medium leading-tight'>
                {banner.title}
              </div>
            )}

            <div className='text-xs'>
              {banner.product_title || banner?.catalogItems?.[0]?.title || ''}
            </div>
          </div>
          <div className='absolute bg-[var(--mid-yellow)] rounded-lg top-[70px] left-[calc(50%_-_10px)] p-1 text-white'>
            <div className='text-[var(--main-font-color)] text-center min-w-[60px]'>
              {oldPrice?.wholePart && banner?.catalogItems?.[0]?.oldPrice ? (
                <div className='text-xs line-through'>
                  {oldPrice?.wholePart} ₽
                </div>
              ) : null}
              <div className='text-base font-medium'>
                {newPrice?.wholePart}
                {newPrice?.floatPart && (
                  <span className='newPriceFloat'>,{newPrice.floatPart}</span>
                )}
                <span className='newPriceFloat'>
                  {' '}
                  ₽
                  {!banner.catalogItems?.[0]?.priceBlockWeightPrice
                    ? null
                    : banner.catalogItems[0]?.measureItem?.symbol
                    ? `/${banner.catalogItems[0]?.measureItem?.symbol}`
                    : null}
                </span>
              </div>
            </div>
            {oldPrice?.wholePart && banner?.catalogItems?.[0]?.oldPrice ? (
              <div className='absolute top-0 -right-5 text-[var(--main-font-color)] bg-white rounded-lg text-xs px-1 py-0.5 font-medium'>
                -{percentDiscount}%
              </div>
            ) : null}
          </div>
          <div className='w-full h-full'>
            <LazyLoadImage
              className='object-cover w-full h-full overflow-hidden rounded-xl'
              src={
                banner?.fileItem?.cacheItem?.[0]?.filePath ||
                banner.bannerFilePath
              }
              visibleByDefault={visibleByDefault}
            />
          </div>
        </>
      ) : (
        <LazyLoadImage
          className='main-slide-banner__img'
          src={
            banner?.file_new?.length === 0
              ? banner?.fileItem?.cacheItem?.[0]?.filePath ||
                banner.bannerFilePath
              : banner.file_new
          }
          alt={banner.title}
        />
      )}
    </BannersLink>
  );
};

export default memo(RubricBanner);
